import React, { useState, useContext, useEffect, Suspense, useMemo, useRef } from 'react'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import qs from 'query-string'
import ls from 'local-storage'
import './App.css'
import { Route, Switch, Link, useLocation, useHistory } from 'react-router-dom'
import { API_KEY } from './fixtures/apiUrls'
import {
  AUTH_ACTION_INITIALIZE,
  AUTH_ACTION_LOGIN,
  AUTH_ACTION_LOGOUT,
  CONVERSATION_ACTION_WS_CONNECT,
  CONVERSATION_ACTION_WS_DISCONNECT,
  PROJECT_ACTION_WS_CONNECT,
  PROJECT_ACTION_WS_DISCONNECT,
  AUTH_ACTION_INIT_VISITOR,
  AUTH_ACTION_REGISTER_USER,
  AUTH_ACTION_GET_BUSINESS_INFO,
  AUTH_ACTION_GET_APP_SETTINGS
} from './store/types'
import { eventSource } from './store'
import {
  useAuth,
  useUserInfo,
  useConversationWSStatus,
  useProjectWSStatus,
  useQueueList,
  WS_STATUS,
} from './store/selectors'
import { useDispatch } from 'react-redux'
import {
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import NavigationController from './navigation'
import { MainViewContactController } from './shared/controllers'
import { SplitView, SplitViewPane, useSnackbarContext, ViewContext } from 'shared/ui'
import { BackIcon } from 'shared/icons'
import { snapshotSidebar, isSameSidebarTarget, pushEntry, matchesExpected } from 'shared/utils/sidebarHistory'
import { appList } from './apps'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
// snackbar
import { useSnackbar } from 'notistack'
// firebase
import { getMessaging, getToken } from 'firebase/messaging'
import { GlobalDebug } from './remove.consoles'
import { Notification, ProjectNotification } from './navigation/Notification'
import alertmsg from "./assets/sounds/alert-msg.wav";

export const AppContext = React.createContext({
  showModalView: (path, viewName, viewProps) => { },
  showStoreModalView: (path, viewName, viewProps) => { },
  showComponentView: (path, viewName, viewProps) => { },
  showSideBar: (path, viewName, viewProps, options) => { },
  closeSideBar: () => { },
  isSidebarOpen: false,
  sideBarProps: null,
  activeStore: null,
  activeShift: null,
  userInfo: null,
  conversationWSStatus: null,
})

export const useAppContext = () => useContext(AppContext)

function DynamicApp({ appRoot, loading, ...appProps }) {
  // return <ContactApp />

  /**
   * Must use useMemo to prevent component rerendering
   * https://stackoverflow.com/questions/56887650/react-lazy-loaded-component-loosing-its-state-gets-unmounted  
   */
  const App = useMemo(() => React.lazy(() => import(`${appRoot}/App`)), [appRoot])

  /**
   * Use to check lazy load
   * leave it commented when not needed, don't delete
   */
  // const App = useMemo(() => React.lazy(() => {
  //   const x = new Promise((resolve) => {
  //     setTimeout(() => {
  //       return resolve(import(`${appRoot}/App`))
  //     }, 2000)
  //   })
  //   return x;
  // }), [appRoot])

  let loadingDiv = loading || (
    <div style={{ textAlign: "center", marginTop: 20 }}>
      ...
    </div>
  )

  return (
    <Suspense fallback={loadingDiv}>
      <App
        eventSource={eventSource}
        {...appProps}
      />
    </Suspense>
  )
}


const initialState = {
  path: null,
  viewName: null,
  viewProps: null
}

// Everything inside the sidebar gets this: AppContext whose showSideBar counts as "opened from inside a sidebar" (so the
// sidebar being left is kept for going back), and a back button in the view's breadcrumbs while there is somewhere to go back
// to. It re-provides the outer ViewContext rather than replacing it, and the apps' SidebarApp wrappers already spread
// viewContext.breadcrumbs after their own.
function SidebarScope({ appContext, canGoBack, onBack, children }) {
  const viewContext = useContext(ViewContext)
  const scopedViewContext = useMemo(() => canGoBack
    ? { ...viewContext, breadcrumbs: [{ icon: BackIcon, insetLeftMargin: true, tooltip: 'Back', onClick: onBack }, ...(viewContext.breadcrumbs || [])] }
    : viewContext,
  [viewContext, canGoBack, onBack])

  return (
    <AppContext.Provider value={appContext}>
      <ViewContext.Provider value={scopedViewContext}>
        {children}
      </ViewContext.Provider>
    </AppContext.Provider>
  )
}

function App() {
  const auth = useAuth()
  const userInfo = useUserInfo()
  const conversationWSStatus = useConversationWSStatus()
  const projectWSStatus = useProjectWSStatus()
  const queueList = useQueueList()
  const [modalView, setModalView] = useState(null)
  const [storeModalView, setStoreModalView] = useState(null)
  const [sideBar, setSideBar] = useState(null)
  const [activePane, setActivePane] = useState(0)
  const [activeStore, setActiveStore] = useState(null)
  const [activeShift, setActiveShift] = useState(null)
  const [storeItems, setStoreItems] = useState([])
  const [fcMessaging, setFcMessaging] = useState(undefined)
  const [fcmToken, setFcmToken] = useState(null)
  const [lastMsgId, setLastMsgId] = useState([])
  const [projectsCtx, setProjectsCtx] = useState([])

  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const navigationControllerRef = useRef()

  /** snackbar hooks and states */
  const { showSnackbar } = useSnackbarContext()
  
  const notifSoundRef = useRef(null)

  /** sidebar related state */
  const [sidebarPath, setSidebarPath] = useState(null)
  const [sidebarViewName, setSidebarViewName] = useState(null)
  const [sidebarViewProps, setSidebarViewProps] = useState(null)
  const [sidebarState, setSidebarState] = useState(initialState)
  // Query-string keys the CURRENTLY-open sidebar's own viewProps contributed (kept in
  // sync by the location.search effect below, from whichever source changed the URL —
  // showSideBar, a raw deep link, or browser back/forward). Used to drop exactly those
  // keys when switching to a sidebar in a different app, instead of letting them leak
  // forward forever (e.g. a Calendar event's `eventKey` surviving into a later Lead
  // sidebar's query params). Same-app view switches (e.g. Calendar's daily list ->
  // event detail, which intentionally carries `date` forward for its back button) are
  // left alone.
  const sidebarOwnedKeysRef = useRef([])
  // Whether showSideBar has run at least once this session — once it has, it's the
  // sole authority for sidebarOwnedKeysRef (see showSideBar and the location.search
  // effect below); an empty array from a call with no extra viewProps shouldn't be
  // mistaken for "never initialized" and cause the effect to reseed it from the URL.
  const sidebarOwnedKeysInitializedRef = useRef(false)
  // Navigation history of the sidebar: the sidebars left by opening another one from inside it (see SidebarScope), oldest
  // first. Kept in a ref too, so a callback running later sees the live stack. Dropped by anything that changes the sidebar
  // from outside — showSideBar from the page, closeSideBar, or the URL moving away from what showSideBar last wrote.
  const [sidebarStack, setSidebarStackState] = useState([])
  const sidebarStackRef = useRef([])
  // What showSideBar last wrote to the URL ({ path, viewName, viewProps }), to tell its own URL changes from outside ones.
  const expectedSidebarRef = useRef(null)
  const sidebarStateRef = useRef(initialState)
  const sidebarOpenRef = useRef(false)
  sidebarStateRef.current = sidebarState
  sidebarOpenRef.current = Boolean(sideBar)

  const setSidebarStack = (next) => {
    if (!next.length && !sidebarStackRef.current.length) return
    sidebarStackRef.current = next
    setSidebarStackState(next)
  }

  const login = () => dispatch({ type: AUTH_ACTION_LOGIN })
  const logout = () => dispatch({ type: AUTH_ACTION_LOGOUT })

  /** condition to check if current route is conversation */
  const isConversation = location.pathname.startsWith("/conversations")

  // Closes whichever sidebar is open. Shared by the sidebar's own close button (see
  // renderSideBar) and by pages that need to dismiss it programmatically (appContext
  // .closeSideBar — e.g. the Tasks page, after deleting the task its sidebar is showing).
  // Reads the live URL (history.location), not a render-time snapshot, so it's safe to
  // call later from an async callback.
  const closeSideBar = () => {
    const { auth_success, auth_token, path, viewName, companyKey, contactKey, companyContactKey, issueKey, projectKey, sidePanelTab, date, ...viewProps } = qs.parse(history.location.search, { parseNumbers: true })

    // Drop the closing sidebar's own entity keys (eventKey, leadKey, ...) too, on
    // top of the small fixed set already excluded above — otherwise they used to
    // linger in the URL past close and could leak into whichever sidebar opens next.
    const remaining = { ...viewProps }
    sidebarOwnedKeysRef.current.forEach(key => { delete remaining[key] })

    setSideBar(null)
    setSidebarState(initialState)
    sidebarOwnedKeysRef.current = []
    expectedSidebarRef.current = null
    setSidebarStack([])
    history.replace({ search: qs.stringify(remaining) })
  }

  // Shows a sidebar: rewrites the URL, which the location.search effect below turns into sidebarState.
  const applySidebar = (path, viewName, viewProps = {}) => {
    const queryParams = qs.parse(history.location.search)
    const { auth_success, auth_token, ...rest } = queryParams
    const { initialData, sidebarProps, ...restProps } = viewProps

    // Leaving this app's sidebar for a different one: strip the outgoing sidebar's
    // own keys so they don't ride along into the new one (see sidebarOwnedKeysRef).
    const samePath = path === sidebarStateRef.current?.path
    if (!samePath)
      sidebarOwnedKeysRef.current.forEach(key => { delete rest[key] })

    /**
     * reason to add sidebarProps
     * here other viewProps aside from ones separated from the rest will be turned into query params
     * and reusing the initialData name feels inappropiate and hack-y
     */
    const newQueries = { ...rest, path, viewName, ...restProps }
    expectedSidebarRef.current = { path, viewName, viewProps: restProps }
    history.replace({ search: qs.stringify(newQueries) })
    setSideBar(true)

    // Track exactly the keys THIS call is contributing — union with whatever was
    // already tracked when staying within the same app (so e.g. Calendar's daily
    // list -> event detail keeps `date` for the create form's default), reset
    // when switching apps. Deliberately NOT derived from the full URL (see the
    // location.search effect below) — that would also sweep in whatever page is
    // open behind the sidebar (e.g. Conversations' own `order`/`q` filters, which
    // just happen to be in the same query string) and wrongly delete them the next
    // time the sidebar closes or switches apps.
    sidebarOwnedKeysRef.current = samePath
      ? Array.from(new Set([...sidebarOwnedKeysRef.current, ...Object.keys(restProps)]))
      : Object.keys(restProps)
    sidebarOwnedKeysInitializedRef.current = true

    setSidebarState(prev => ({
      ...prev,
      initialData,
      sidebarProps
    }))
  }

  // `fromSidebar`: the caller lives inside the sidebar (SidebarScope's showSideBar). Opening another sidebar from there keeps
  // the one being left to go back to; the same sidebar again (a tab switch) just updates it; opening the one it came from
  // (an edit form saving back to its detail) is going back; from anywhere else the history starts over.
  // `replace`: the sidebar being left is not worth coming back to (a create form that just created its record).
  const openSidebar = (path, viewName, viewProps = {}, { fromSidebar = false, replace = false } = {}) => {
    const stack = sidebarStackRef.current
    const current = sidebarOpenRef.current ? snapshotSidebar(sidebarStateRef.current, sidebarOwnedKeysRef.current) : null
    const next = { path, viewName, viewProps }
    if (!fromSidebar || !current)
      setSidebarStack([])
    else if (isSameSidebarTarget(current, next) || replace)
      return applySidebar(path, viewName, viewProps)
    else if (stack.length && isSameSidebarTarget(stack[stack.length - 1], next))
      setSidebarStack(stack.slice(0, -1))
    else
      setSidebarStack(pushEntry(stack, current))
    applySidebar(path, viewName, viewProps)
  }

  const goBackSideBar = () => {
    const stack = sidebarStackRef.current
    const entry = stack[stack.length - 1]
    if (!entry) return
    setSidebarStack(stack.slice(0, -1))
    applySidebar(entry.path, entry.viewName, { ...entry.viewProps, initialData: entry.initialData, sidebarProps: entry.sidebarProps })
  }

  const appContext = {
    closeSideBar,
    showModalView: (path, viewName, viewProps) => setModalView({ path, viewName, viewProps }),
    showStoreModalView: (path, viewName, viewProps) => setStoreModalView({ path, viewName, viewProps }),
    showComponentView: (path, viewName, viewProps) => renderComponentView({ path, viewName, viewProps }),
    showSideBar: (path, viewName, viewProps) => openSidebar(path, viewName, viewProps),
    isSidebarOpen: sideBar,
    sideBarProps: sidebarState,
    activeStore,
    setActiveStore,
    activeShift,
    setActiveShift,
    storeItems,
    setStoreItems,
    userInfo,
    conversationWSStatus,
    projectWSStatus,
    projectsCtx,
    setProjectsCtx
  }

  // The context the sidebar's own views get: same as appContext, but what they open counts as navigation inside the sidebar.
  const sidebarAppContext = {
    ...appContext,
    showSideBar: (path, viewName, viewProps, options) => openSidebar(path, viewName, viewProps, { ...options, fromSidebar: true }),
  }

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await window.Notification.requestPermission()
    }
  }

  useEffect(() => {
    dispatch({ type: AUTH_ACTION_INITIALIZE })
    requestNotificationPermission()

    notifSoundRef.current = new Audio(alertmsg)
    notifSoundRef.current.preload = "auto"

    if (process.env.NODE_ENV === 'production') {
      GlobalDebug(false)
    }

    /** get firebase messaging instance */
    try {
      const messaging = getMessaging()
      if (messaging)
        setFcMessaging(messaging)
    } catch (err) {
      console.log('firebase cannot be initialized');
    }

    /**
     * use to test snackbar without needing to send/edit chats or issues
     * leave it commented when not needed, don't delete
     */
    // setTimeout(() => {
    //   Array.from({ length: 5 }).forEach(i => {
    //     showSnackbar('Test Notification', {
    //       anchorOrigin: { horizontal: 'right', vertical: 'top' },
    //       content: (key, msg) => <Notification notificationKey={key} {...{ key, msg, body: "This is a dummy test notification" }} />
    //     })
    //   })
    // }, 3000)
  }, [])

  useEffect(() => {

    const onConversationWSMessage = ({ type, data }) => {
      /** Forward conversation WS message to our snackbar */
      if (type === 'new_message') {
        const isInParties = data?.conversation?.parties.some(item => item.agent?.uid == userInfo?.uid)
        const isCurrentConversation = location.pathname.includes(data?.conversation?.uid)
        const isLastMsgId = lastMsgId.some(item => item == data?.id)
        const isInQueue = queueList?.data?.some(item => item.uid == data?.conversation?.queue?.uid)

        if (
          (document.hidden || !isCurrentConversation) &&
          !isLastMsgId &&
          data?.sender?.agent?.uid !== userInfo?.uid &&
          (isInParties || (data?.conversation?.assigned_at && !data?.conversation?.assigned_to && isInQueue))
        ) {

          let newLastMsgId = lastMsgId.length == 20 ? [...lastMsgId.splice(1, 19), data?.id] : [...lastMsgId, data?.id]
          setLastMsgId(newLastMsgId)

          if (notifSoundRef?.current) notifSoundRef?.current?.play()

          showSnackbar('New Message', {
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            content: (key, msg) => <Notification notificationKey={key} {...{ key, msg, data }} />
          })

        }
      }
      else if (type === 'new_assignment') {
        if (notifSoundRef?.current) notifSoundRef?.current?.play()

        showSnackbar('New Assignment', {
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
          content: (key, msg) => <Notification notificationKey={key} {...{ data, key, msg, body: "You have been assigned new conversation with customer" }} />
        })
      }

      /** fetch user notification on WS notif */
      // navigationControllerRef?.current?.getNotificationsUser()
    }

    const onProjectWSMessage = ({ type, data }) => {
      if (type === 'new_assignment') {
        if (notifSoundRef?.current) notifSoundRef?.current?.play()

        showSnackbar('New Assignment', {
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
          content: (key, msg) => <ProjectNotification notificationKey={key} {...{ data, key, msg, body: "You have been assigned to a new issue" }} />
        })
      } else if (type === 'new_comment') {
        if (userInfo?.uid !== data?.created_by?.uid) {
          if (notifSoundRef?.current) notifSoundRef?.current?.play()
          showSnackbar('New Comment', {
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            content: (key, msg) => <ProjectNotification notificationKey={key} {...{ data, key, msg }} />
          })
        }
      }

      /** fetch user notification on WS notif */
      navigationControllerRef?.current?.getNotificationsUser()
    }

    /** listen to new messages & projects */
    eventSource.addEventListener("message", onConversationWSMessage)
    eventSource.addEventListener("project", onProjectWSMessage)

    return () => {
      eventSource.removeEventListener("message", onConversationWSMessage)
      eventSource.removeEventListener("project", onProjectWSMessage)
    }
  }, [lastMsgId, isConversation, userInfo, location.pathname, queueList])

  useEffect(() => {
    // getFirebaseToken()
  }, [fcMessaging])

  const getFirebaseToken = () => {
    if (fcMessaging && !fcmToken)
      try {
        getToken(fcMessaging, { vapidKey: 'BAMSIiijs9V_euCneshqzfTIpLOTeKnI2jStz0IDem0-hb2qRYbYRTPuxo79POziYHYwMYf84LRQsBmi6e_o3kA' })
          .then((currentToken) => {
            if (currentToken) {
              setFcmToken(currentToken)
            } else {
              // Show permission request UI
              console.log('No registration token available. Request permission to generate one.');
            }
          })
          .catch((err) => {
            console.log('An error occurred while retrieving token. ', err);
          })
      } catch (error) {
        console.log('firebase instance not defined');
      }
  }

  useEffect(() => {
    const savedFcmToken = ls.get('_fcmToken')

    if (fcmToken) {
      if (!savedFcmToken || savedFcmToken !== fcmToken) {
        ls.set('_fcmToken', fcmToken)

        // re-register after getting token
        if (auth?.isLoggedIn && auth?.goid) {
          const payload = {
            _goid: auth?.goid,
            external_app: 'alpha.apps.directory',
            external_model: 'user',
            external_id: userInfo?.uid,
            first_name: userInfo?.first_name,
            last_name: userInfo?.last_name,
            email: userInfo?.email,
            mobile_no: userInfo?.mobile_no
          }
          dispatch({
            type: AUTH_ACTION_REGISTER_USER,
            payload
          })
        }
      }
    }
  }, [fcmToken])

  const getWSHost = (service) => {
    let host = process.env.REACT_APP_GOAPP_API_URL + "/ws/" + service + "/"
    host = host.replace("http://", "ws://")
    host = host.replace("https://", "wss://")
    host += "?"
    host += "&api_key=" + API_KEY
    host += "&token=" + auth.token
    return host
  }

  const [isOnline, setIsOnline] = useState(false)

  useEffect(() => {
    window.addEventListener('online', (e) => {
      setIsOnline(true)
    })

    window.addEventListener('offline', (e) => {
      setIsOnline(false)
      dispatch({
        type: CONVERSATION_ACTION_WS_DISCONNECT
      })
    })

    return () => {
    }
  }, [])

  useEffect(() => {
    if (!auth.isLoggedIn)
      return

    if (!auth?.goid) {
      dispatch({ type: AUTH_ACTION_INIT_VISITOR })
    }
    else {
      getFirebaseToken()
      dispatch({ type: AUTH_ACTION_GET_BUSINESS_INFO })
      dispatch({ type: AUTH_ACTION_GET_APP_SETTINGS })

      const payload = {
        _goid: auth?.goid,
        external_app: 'alpha.apps.directory',
        external_model: 'user',
        external_id: userInfo?.uid,
        first_name: userInfo?.first_name,
        last_name: userInfo?.last_name,
        email: userInfo?.email,
        mobile_no: userInfo?.mobile_no
      }
      dispatch({
        type: AUTH_ACTION_REGISTER_USER,
        payload
      })
    }
  }, [auth.isLoggedIn, auth.goid])

  useEffect(() => {
    if (!auth.isLoggedIn)
      return

    if (conversationWSStatus === WS_STATUS.Idle || conversationWSStatus === WS_STATUS.Disconnected) {
      let timeout = 250
      if (!isOnline && conversationWSStatus === WS_STATUS.Disconnected)
        timeout = 3000

      setTimeout(() => {
        dispatch({
          type: CONVERSATION_ACTION_WS_CONNECT,
          host: getWSHost("conversation"),
        })
        //        alert(getWSHost())
        // props.wsConnect(getWSHost())
      }, timeout)
    }
    else if (conversationWSStatus == WS_STATUS.Connected) {
      // // Refresh conversation list on re-connect.
      // fetchConversationCounts(true)      
    }

    if (projectWSStatus === WS_STATUS.Idle || projectWSStatus === WS_STATUS.Disconnected) {
      let timeout = 250
      if (!isOnline && conversationWSStatus === WS_STATUS.Disconnected)
        timeout = 3000

      setTimeout(() => {
        dispatch({
          type: PROJECT_ACTION_WS_CONNECT,
          host: getWSHost("project"),
        })
        // alert(getWSHost())
        // props.wsConnect(getWSHost())
      }, timeout)
    }
    else if (projectWSStatus == WS_STATUS.Connected) {
      /** Refresh conversation list on re-connect. (deprecated for now?) */
      // fetchConversationCounts(true)      
    }
  }, [auth.isLoggedIn, conversationWSStatus, projectWSStatus, isOnline])

  /** sidebar effect block */
  useEffect(() => {
    const queryParams = qs.parse(location.search, { parseNumbers: true })
    const { auth_success, auth_token, path, viewName, ...viewProps } = queryParams

    let newState = JSON.parse(JSON.stringify(sidebarState))

    // The sidebar in the URL isn't what showSideBar last wrote (the conversation list swapping the record, browser
    // back/forward, a deep link): changed from outside, so its navigation history no longer applies.
    if (sidebarStackRef.current.length && ((!path && !viewName) || !matchesExpected(expectedSidebarRef.current, { path, viewName, viewProps })))
      setSidebarStack([])

    if (!path && !viewName) {
      // Sidebar identity fell out of the URL entirely — most often some unrelated page
      // rewriting the whole query string for its own filters/navigation and dropping
      // `path`/`viewName` along the way. Previously this branch did nothing, leaving
      // sidebarState (and the mounted sidebar) stuck pointing at whatever was open
      // before, while its viewProps still got overwritten below — the sidebar stayed
      // open but broken (empty or perpetually "loading", since its own key, e.g.
      // eventKey, had just vanished). Close cleanly instead.
      if (sideBar) setSideBar(false)
      newState = initialState
      sidebarOwnedKeysRef.current = []
    } else {
      newState = { ...newState, path: path || null, viewName: viewName || null, viewProps }

      // Only seed ownership tracking here when showSideBar has never run this session —
      // a raw deep link or browser back/forward landing on a sidebar URL. Checking
      // "was the sidebar previously closed" instead (as this used to) is true on every
      // ordinary showSideBar-driven open too, since sidebarState hasn't been updated to
      // the new path/viewName yet when this effect runs — so it clobbered the precise
      // set showSideBar had *just* written with every key currently in the URL,
      // including whatever page is open behind the sidebar (e.g. Conversations' own
      // filter params, which just happen to share the query string) — and then closing
      // or switching deleted those too (observed as the conversation list losing its
      // filters and doing a full reload on close).
      if (!sidebarOwnedKeysInitializedRef.current) {
        sidebarOwnedKeysRef.current = Object.keys(viewProps)
        // One-shot: a deep link that's never followed by a showSideBar call (e.g. the
        // sidebar is closed without any interaction) would otherwise keep re-seeding
        // from the ambient URL on every later location.search change — including ones
        // from the page behind it changing its own filters — reopening the same bug
        // for that narrow case.
        sidebarOwnedKeysInitializedRef.current = true
      }
    }

    setSidebarState(newState)

    if (!sidebarState?.viewName && !sidebarState?.path && path === 'contacts' &&
      (viewName === 'contact-sidebar' || viewName === 'company-sidebar')) {
      setSideBar(true)
    }
  }, [location.search])

  const renderChildApp = (app) => (props) => {
    // Why div below can't use provided className??
    return (
      <div
        className="page"
        style={{ position: "absolute", width: "100%" }}
      >
        <DynamicApp {...props} appRoot={app.appRoot} />
      </div>
    )
  }

  const renderModalView = (modalView) => {
    const app = appList.find(a => a.path === modalView.path)

    return (
      <DynamicApp
        appRoot={app.appRoot}
        modal={true}
        onClose={() => setModalView(null)}
        {...modalView}
      />
    )
  }

  const renderComponentView = (storeModalView) => {
    const app = appList.find(a => a.path === storeModalView.path)

    return (
      <DynamicApp
        appRoot={app.appRoot}
        component={true}
        onClose={() => setStoreModalView(null)}
        {...storeModalView}
      />
    )
  }

  const renderStoreModalView = (storeModalView) => {
    const app = appList.find(a => a.path === storeModalView.path)

    return (
      <DynamicApp
        appRoot={app.appRoot}
        modal={true}
        onClose={() => setStoreModalView(null)}
        {...storeModalView}
      />
    )
  }

  const renderSideBar = () => {
    const queryParams = qs.parse(location.search, { parseNumbers: true })
    const { auth_success, auth_token, path, viewName, companyKey, contactKey, companyContactKey, issueKey, projectKey, sidePanelTab, date, ...viewProps } = queryParams
    const app = appList.find(a => a.path === sidebarState?.path)

    const loading = (
      <div className="sidebar-fill">
        <div style={{ textAlign: "center", marginTop: 20 }}>
          ...
        </div>
      </div>
    )

    const closeSidebar = closeSideBar
    const canGoBack = sidebarStack.length > 0

    if (smDown) {
      if (sideBar)
        return (
          // Same "sidebar-fill" opaque full-coverage backdrop the desktop branch
          // below already wraps its own DynamicApp in — this one was missing it
          // entirely. A genuine MUI Dialog-based modal (create-lead, select-issue,
          // ...) escapes this via its own portal to document.body regardless, so
          // this only actually affects the sidebar-shaped full-page components
          // (LeadDetail, IssueDetail, ...) that render inline rather than portaling
          // — those have no opaque background of their own, so without this wrapper
          // they rendered directly on top of whatever page was already mounted
          // underneath, with both bleeding through each other. Reported as "in
          // mobile web, navigating to lead detail from issue detail, weirdly looks
          // like it's overlayed".
          <div className="sidebar-fill">
            <SidebarScope appContext={sidebarAppContext} canGoBack={canGoBack} onBack={goBackSideBar}>
              <DynamicApp
                appRoot={app?.appRoot}
                modal={true}
                onClose={closeSidebar}
                loading={loading}
                viewName={sidebarState?.viewName}
                companyKey={sidebarState?.companyKey}
                companyContactKey={sidebarState?.companyContactKey}
                contactKey={sidebarState?.contactKey}
                issueKey={sidebarState?.issueKey}
                projectKey={sidebarState?.projectKey}
                sidePanelTab={sidebarState?.sidePanelTab}
                viewProps={sidebarState?.viewProps}
                sidebarProps={sidebarState?.sidebarProps}
                date={sidebarState?.date}
              />
            </SidebarScope>
          </div>
        )
      else
        return
    }
    else {
      /**
       * 20 Dec 2021 - If we add SplitViewPane based on side bar existance, it will...
       * ...recreate content pane component every time we show or hide sidebar. Use...
       * ...hidden prop solve this issue
       */
      return (
        <SplitViewPane pane="side" hidden={!sideBar}>
          {sideBar &&
            <div className="sidebar-fill">
              <SidebarScope appContext={sidebarAppContext} canGoBack={canGoBack} onBack={goBackSideBar}>
                <DynamicApp
                  appRoot={app?.appRoot}
                  sideBar={true}
                  onClose={closeSidebar}
                  loading={loading}
                  viewName={sidebarState?.viewName}
                  companyKey={sidebarState?.companyKey}
                  companyContactKey={sidebarState?.companyContactKey}
                  contactKey={sidebarState?.contactKey}
                  issueKey={sidebarState?.issueKey}
                  projectKey={sidebarState?.projectKey}
                  viewProps={sidebarState?.viewProps}
                  sidebarProps={sidebarState?.sidebarProps}
                />
              </SidebarScope>
            </div>
          }
        </SplitViewPane>
      )
    }
  }

  const redirectToLogin = () => {
    const queryParams = qs.parse(location.search)
    const { auth_success, isloggedout } = queryParams

    if (auth_success !== undefined && auth_success == 0) {
      return <>
        <div style={{ marginBottom: '10px' }}>
          Login Failed.
        </div>
        <Button variant="contained" onClick={login}>Login</Button>
      </>
    } else if (isloggedout) {
      return <>
        <div style={{ marginBottom: '10px' }}>
          You have been logged out.
        </div>
        <Button variant="contained" onClick={login}>Login</Button>
      </>
    } else {
      setTimeout(() => {
        login()
      }, 3000);

      return "You will be redirected to login page, please wait…"
    }
  }

  if (!auth.isInitialized)
    return (
      <div className="App" style={{ textAlign: "center", marginTop: 10 }}>
        ...
      </div>
    )
  else if (!auth.isLoggedIn)
    return (
      <div className="App" style={{ textAlign: "center", marginTop: 10 }}>
        {auth.isAuthenticating ?
          "Authenticating..."
          :
          redirectToLogin()
        }
      </div>
    )

  /** Automatically redirect to "/home" */
  if (location.pathname === "/")
    history.push("/home")

  const childApp = appList.find(app => location.pathname.startsWith("/" + app.path)) || ""

  return (
    <div className="App">
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <AppContext.Provider value={appContext}>
          <NavigationController ref={navigationControllerRef}>
            {smDown ?
              <>
                <Switch location={location}>
                  {appList.map(app =>
                    <Route path={`/${app.path}`} render={renderChildApp(app)} />
                  )}
                </Switch>
                {renderSideBar()}
              </>
              :
              <SplitView divider={null} activePane={activePane} onSetActivePane={setActivePane} fixedPosition={true}>

                {/* MAIN PANE CONTAINS ROUTE CHILDRES */}
                <SplitViewPane pane="content" fixedPosition={true}>
                  <Switch location={location}>
                    {appList.map(app =>
                      <Route path={`/${app.path}`} render={renderChildApp(app)} />
                    )}
                  </Switch>
                </SplitViewPane>

                {renderSideBar()}

              </SplitView>
            }

            {modalView &&
              renderModalView(modalView)
            }

            {storeModalView &&
              renderStoreModalView(storeModalView)
            }
          </NavigationController>
        </AppContext.Provider>
      </MuiPickersUtilsProvider>
    </div>
  )

  return (
    <div className="App">
      <AppContext.Provider value={appContext}>
        <NavigationController>
          <TransitionGroup>
            <CSSTransition key={childApp.path} timeout={300} classNames="page">
              <Switch location={location}>
                {appList.map(app =>
                  <Route path={`/${app.path}`} render={renderChildApp(app)} />
                )}
              </Switch>
            </CSSTransition>
          </TransitionGroup>

          {modalView &&
            renderModalView(modalView)
          }

        </NavigationController>
      </AppContext.Provider>
    </div>
  )
}

export default App